// Angular Files
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Other External Files
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Angular Material Files
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

// Teller Online Files
import { AuthService, ConfigurationService, PublicPortalSiteModel } from 'apps/admin-portal/src/app/core/services';
import { ConfigImportWizardComponent } from 'apps/admin-portal/src/app/features/config/dialogs/config-import-wizard/config-import-wizard.component';

// Teller Online Library Files
import { TellerOnlineAppService, TellerOnlineSiteMetadataService } from 'teller-online-libraries/core';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    host: {
        class: 'header'
    }
})
export class HeaderComponent implements OnInit, OnDestroy {
    // Input properties
    @Input() public helpOpened: boolean = false;

    // Output events
    @Output() public toggleHelp = new EventEmitter<void>();

    // Public variables
    public siteConfig: PublicPortalSiteModel;
    public isExporting: boolean = false;

    // Private variables
    private _destroy$ = new Subject<void>();
    constructor(
        public authService: AuthService,
        public appService: TellerOnlineAppService,
        public siteMetadataService: TellerOnlineSiteMetadataService,
        private route: ActivatedRoute,
        private http: HttpClient,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private configurationService: ConfigurationService) {
    }

    async ngOnInit() {
        this.siteConfig = await firstValueFrom(this.configurationService.getSite());
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public onClick_signOut(): void {
        this.authService.signOut();
    }

    public get logo (): string {
        return !this.appService.currentPage('/config')
            ? 'assets/images/logo.svg'
            : 'assets/images/logo-config.svg';
    }

    public get homeLink (): string {
        return !this.appService.currentPage('/config')
            ? '/dashboard'
            : '/config';
    }
    
    
    public onClick_export() {
        if (this.isExporting || !this.siteConfig?.id) {
            return;
        }

        this.isExporting = true;
        const url = `api/admin-portal/config/public-portal-sites/${this.siteConfig.id}/configuration/export`;

        this.http.get(url, { responseType: 'blob', observe: 'response', withCredentials: true })
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response) => {
                    this.isExporting = false;

                    // Extract filename from Content-Disposition header or use default
                    const contentDisposition = response.headers.get('Content-Disposition');
                    let filename = `${this.siteConfig.tenantName || 'config'}-export.zip`;
                    if (contentDisposition) {
                        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                        if (matches && matches[1]) {
                            filename = matches[1].replace(/['"]/g, '');
                        }
                    }

                    // Trigger file download
                    const blob = response.body;
                    if (blob) {
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = filename;
                        link.click();
                        window.URL.revokeObjectURL(downloadUrl);
                    }

                    this.snackBar.open('Configuration exported successfully', 'Close', { duration: 3000 });
                },
                error: (error) => {
                    this.isExporting = false;
                    this.snackBar.open('Failed to export configuration', 'Close', { duration: 5000 });
                    console.error('Export error:', error);
                }
            });
    }

    public onClick_import() {
        if (!this.siteConfig?.id) {
            return;
        }

        const dialogRef = this.dialog.open(ConfigImportWizardComponent, {
            width: '600px',
            disableClose: true,
            data: { siteId: this.siteConfig.id, tenantName: this.siteConfig.tenantName }
        });

        dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe(result => {
            if (result?.imported) {
                // Reload the page to get the updated configuration
                window.location.reload();
            }
        });
    }

    public onClick_help(): void {
        if(!this.appService.currentUrl.includes("help")) this.toggleHelp.emit();
    }
}
